import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Dialog,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import api from '../services/api';
import {
  UserMessage,
  AgentEventBubble,
  AgentThinkingIndicator,
  CompletionBanner,
} from './chat/ChatBubbles';

/* ================================================================== */
/*  Revision Chat Dialog                                               */
/* ================================================================== */

const REVISION_POLL_MS = 3000;
const REVISION_DONE_STATUSES = new Set(['awaiting_review', 'failed', 'completed', 'rejected']);

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(Object.assign(new Error('Aborted'), { name: 'AbortError' }));
    };
    if (signal?.aborted) {
      onAbort();
      return;
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

export default function RevisionChatDialog({ open, onClose, job, onRevisionComplete, chatCacheRef }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [latestReviewContext, setLatestReviewContext] = useState(null);
  const messagesEndRef = useRef(null);
  const abortRef = useRef(null);
  const inputRef = useRef(null);
  const activeJobIdRef = useRef(null);

  /* ── Persist messages to the cache whenever they change ────────── */
  useEffect(() => {
    if (job?.id && job.id === activeJobIdRef.current && messages.length > 0 && chatCacheRef?.current) {
      chatCacheRef.current[job.id] = { messages, latestReviewContext };
    }
  }, [messages, latestReviewContext, job?.id, chatCacheRef]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  /* ── Restore cached messages on open, or start fresh ──────────── */
  useEffect(() => {
    if (open && job?.id) {
      activeJobIdRef.current = job.id;
      const cached = chatCacheRef?.current?.[job.id];
      if (cached && cached.messages.length > 0) {
        setMessages(cached.messages);
        setLatestReviewContext(cached.latestReviewContext || null);
      } else {
        setMessages([]);
        setLatestReviewContext(null);
      }
      setInput('');
      setIsStreaming(false);
    }
  }, [open, job?.id, chatCacheRef]);

  const handleSend = useCallback(async () => {
    const feedback = input.trim();
    if (!feedback || isStreaming || !job) return;

    setInput('');
    setLatestReviewContext(null);

    setMessages((prev) => [...prev, { role: 'user', text: feedback }]);

    setIsStreaming(true);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await api.reviseHealJob(job.id, feedback);
      while (!controller.signal.aborted) {
        await sleep(REVISION_POLL_MS, controller.signal);
        const updated = await api.pollHealJob(job.id);
        if (REVISION_DONE_STATUSES.has(updated.status)) {
          setLatestReviewContext(updated.review_context || null);
          if (onRevisionComplete) onRevisionComplete(job.id);
          break;
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages((prev) => [
          ...prev,
          { role: 'agent', event: { type: 'error', message: err.message } },
        ]);
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [input, isStreaming, job, onRevisionComplete]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClose = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          background: '#0f172a',
          color: '#e2e8f0',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 2,
          width: '680px',
          maxWidth: '90vw',
          height: '80vh',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.5,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.92rem', color: '#e2e8f0' }}>
            Revision
          </Typography>
          {job && (
            <Typography sx={{ fontSize: '0.78rem', color: '#475569' }}>
              {job.retailer} / {job.attribute}
            </Typography>
          )}
          {isStreaming && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 0.5 }}>
              <CircularProgress size={10} thickness={5} sx={{ color: '#475569' }} />
              <Typography sx={{ color: '#475569', fontSize: '0.72rem' }}>
                Running
              </Typography>
            </Box>
          )}
        </Box>
        <Tooltip title="Close">
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{ color: '#475569', '&:hover': { color: '#94a3b8' } }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Messages area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          py: 1,
          '&::-webkit-scrollbar': { width: 5 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.08)', borderRadius: 3 },
        }}
      >
        {/* Empty state */}
        {messages.length === 0 && (
          <Box sx={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            height: '100%', gap: 1, px: 4,
          }}>
            <Typography sx={{ color: '#334155', fontSize: '0.85rem', textAlign: 'center', maxWidth: 340 }}>
              Describe how the repair config should be revised. The agent will make changes and verify them.
            </Typography>
          </Box>
        )}

        {/* Messages */}
        {messages.map((msg, i) =>
          msg.role === 'user' ? (
            <UserMessage key={i} text={msg.text} />
          ) : (
            <AgentEventBubble key={i} event={msg.event} />
          ),
        )}

        {isStreaming && <AgentThinkingIndicator />}

        {!isStreaming && latestReviewContext && (
          <CompletionBanner reviewContext={latestReviewContext} />
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: '1px solid rgba(255,255,255,0.06)',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={4}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isStreaming ? 'Waiting for agent...' : 'Describe what to change...'}
            disabled={isStreaming}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#e2e8f0',
                fontSize: '0.85rem',
                borderRadius: 1.5,
                background: 'rgba(255,255,255,0.02)',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.06)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
              },
              '& .MuiOutlinedInput-input::placeholder': {
                color: '#334155',
                opacity: 1,
              },
            }}
          />
          <Tooltip title={isStreaming ? 'Agent is running' : 'Send (Enter)'}>
            <span>
              <IconButton
                onClick={handleSend}
                disabled={isStreaming || !input.trim()}
                size="small"
                sx={{
                  color: '#64748b',
                  width: 36,
                  height: 36,
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: 1.5,
                  '&:hover': { background: 'rgba(255,255,255,0.04)', color: '#94a3b8' },
                  '&.Mui-disabled': { color: '#1e293b', borderColor: 'rgba(255,255,255,0.03)' },
                }}
              >
                <SendIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Typography sx={{ color: '#1e293b', fontSize: '0.68rem', mt: 0.5, textAlign: 'center' }}>
          Enter to send · Shift+Enter for new line
        </Typography>
      </Box>
    </Dialog>
  );
}
